<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { useMapStore } from '@/stores/map';
import { useEditorStore, EditorTool } from '@/stores/editor';
import { useTools } from '@/composables/useTools';
import Toolbar from './Toolbar.vue';
import MapViewport from './MapViewport.vue';
import TilePalette from './TilePalette.vue';
import SpritesheetViewer from './SpritesheetViewer.vue';
import ObjectSelector from './ObjectSelector.vue';
import PropertiesPanel from './PropertiesPanel.vue';
import Minimap from './Minimap.vue';
import StatusBar from './StatusBar.vue';
import NewMapDialog from './NewMapDialog.vue';

const mapStore = useMapStore();
const editorStore = useEditorStore();
const tools = useTools();

const showNewMapDialog = ref(false);
const isDraggingFile = ref(false);
const minimapFloating = ref(false);

// Right sidebar resize state
const rightSidebarWidth = ref(320);
const isResizingRightSidebar = ref(false);
const minRightSidebarWidth = 200;
const maxRightSidebarWidth = 800;

function startRightSidebarResize(e: MouseEvent): void {
    e.preventDefault();
    isResizingRightSidebar.value = true;
    document.addEventListener('mousemove', handleRightSidebarResize);
    document.addEventListener('mouseup', stopRightSidebarResize);
}

function handleRightSidebarResize(e: MouseEvent): void {
    if (!isResizingRightSidebar.value) return;

    const newWidth = window.innerWidth - e.clientX;
    rightSidebarWidth.value = Math.max(minRightSidebarWidth, Math.min(maxRightSidebarWidth, newWidth));
}

function stopRightSidebarResize(): void {
    isResizingRightSidebar.value = false;
    document.removeEventListener('mousemove', handleRightSidebarResize);
    document.removeEventListener('mouseup', stopRightSidebarResize);
}

/** Replacing the open map loses unsaved edits - ask first */
function confirmDiscardChanges(): boolean {
    return !mapStore.isDirty || window.confirm('The current map has unsaved changes. Discard them?');
}

// Drag and drop
function handleDragOver(e: DragEvent): void {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer?.types.includes('Files')) {
        isDraggingFile.value = true;
        e.dataTransfer.dropEffect = 'copy';
    }
}

function handleDragLeave(e: DragEvent): void {
    e.preventDefault();
    e.stopPropagation();
    isDraggingFile.value = false;
}

async function handleDrop(e: DragEvent): Promise<void> {
    e.preventDefault();
    e.stopPropagation();
    isDraggingFile.value = false;

    const file = e.dataTransfer?.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.amd')) {
        mapStore.lastError = `${file.name} is not an .amd map file`;
        return;
    }

    if (confirmDiscardChanges()) {
        await mapStore.loadMap(file);
    }
}

// File operations
function handleOpen(): void {
    if (confirmDiscardChanges()) {
        void mapStore.openMapWithPicker();
    }
}

function handleSave(saveAs: boolean = false): void {
    void mapStore.saveMap(saveAs);
}

function handleNew(): void {
    showNewMapDialog.value = true;
}

function handleCreateMap(width: number, height: number, terrain: number): void {
    if (!confirmDiscardChanges()) return;

    void mapStore.newMap(width, height, terrain);
    editorStore.clearSelection();
    showNewMapDialog.value = false;
}

// Keyboard shortcuts
const TOOL_SHORTCUTS: Record<string, EditorTool> = {
    v: EditorTool.SELECT,
    b: EditorTool.PAINT_TILE,
    o: EditorTool.PAINT_OBJECT,
    e: EditorTool.ERASE_OBJECT,
    i: EditorTool.EYEDROPPER,
    f: EditorTool.BUCKET_FILL,
    x: EditorTool.TOGGLE_BLOCKED,
    t: EditorTool.TOGGLE_TELEPORT,
    r: EditorTool.TOGGLE_FARMING,
    u: EditorTool.TOGGLE_BUILDABLE,
};

function isTypingTarget(target: EventTarget | null): boolean {
    const tag = (target as HTMLElement | null)?.tagName;
    return tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA';
}

function handleKeydown(e: KeyboardEvent): void {
    // Leave typing alone, and everything while a dialog is up
    if (isTypingTarget(e.target) || showNewMapDialog.value) return;

    const key = e.key.toLowerCase();

    // Ctrl/Cmd shortcuts
    if (e.ctrlKey || e.metaKey) {
        switch (key) {
            case 'n':
                e.preventDefault();
                handleNew();
                break;
            case 'o':
                e.preventDefault();
                handleOpen();
                break;
            case 's':
                e.preventDefault();
                handleSave(e.shiftKey);
                break;
            case 'z':
                e.preventDefault();
                if (e.shiftKey) {
                    mapStore.redo();
                } else {
                    mapStore.undo();
                }
                break;
            case 'y':
                e.preventDefault();
                mapStore.redo();
                break;
            case 'c':
                if (editorStore.hasSelection) {
                    e.preventDefault();
                    tools.copySelection();
                }
                break;
            case 'v':
                if (editorStore.clipboard) {
                    e.preventDefault();
                    tools.paste();
                }
                break;
        }
        return;
    }

    if (e.altKey) return;

    // Space held = pan with the left button
    if (e.key === ' ') {
        e.preventDefault();
        editorStore.panMode = true;
        return;
    }

    const tool = TOOL_SHORTCUTS[key];
    if (tool) {
        editorStore.setTool(tool);
        return;
    }

    switch (e.key) {
        case 'g':
        case 'G':
            editorStore.showGrid = !editorStore.showGrid;
            break;
        case '[':
            editorStore.setBrushSize(editorStore.brushSize - 1);
            break;
        case ']':
            editorStore.setBrushSize(editorStore.brushSize + 1);
            break;
        case '+':
        case '=':
            editorStore.zoomIn();
            break;
        case '-':
            editorStore.zoomOut();
            break;
        case '0':
            editorStore.setZoom(1);
            break;
        case 'Escape':
            editorStore.clearSelection();
            break;
        case 'Delete':
        case 'Backspace':
            tools.clearSelectionObjects();
            break;
    }
}

function handleKeyup(e: KeyboardEvent): void {
    if (e.key === ' ') {
        editorStore.panMode = false;
    }
}

// Space can be released while another window has focus
function handleWindowBlur(): void {
    editorStore.panMode = false;
}

function handleBeforeUnload(e: BeforeUnloadEvent): void {
    if (mapStore.isDirty) {
        e.preventDefault();
    }
}

// On window, not the layout element: shortcuts must work wherever focus is
onMounted(() => {
    window.addEventListener('keydown', handleKeydown);
    window.addEventListener('keyup', handleKeyup);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('beforeunload', handleBeforeUnload);
});

onUnmounted(() => {
    window.removeEventListener('keydown', handleKeydown);
    window.removeEventListener('keyup', handleKeyup);
    window.removeEventListener('blur', handleWindowBlur);
    window.removeEventListener('beforeunload', handleBeforeUnload);
    document.removeEventListener('mousemove', handleRightSidebarResize);
    document.removeEventListener('mouseup', stopRightSidebarResize);
});
</script>

<template>
    <div
        class="editor-layout"
        @dragover="handleDragOver"
        @dragleave="handleDragLeave"
        @drop="handleDrop"
    >
        <!-- Drop zone overlay -->
        <div class="drop-zone-overlay" v-if="isDraggingFile">
            <div class="drop-zone-content">
                <div class="drop-icon">📂</div>
                <p>Drop .amd file to open</p>
            </div>
        </div>

        <!-- Menu bar -->
        <header class="menu-bar">
            <div class="menu-left">
                <a href="http://helbreathx.net" target="_blank" class="logo-link" title="Visit HelbreathX">
                    <img src="/favicon.png" alt="HelbreathX" class="logo-img" />
                </a>
                <a href="http://discord.gg/helbreath" target="_blank" class="discord-link" title="Join our Discord">
                    Join our Discord
                </a>
                <div class="menu-items">
                    <button class="menu-btn" @click="handleNew" title="New (Ctrl+N)">New</button>
                    <button class="menu-btn" @click="handleOpen" title="Open (Ctrl+O)">Open</button>
                    <button class="menu-btn" @click="handleSave()" :disabled="!mapStore.hasMap" title="Save (Ctrl+S)">Save</button>
                    <button class="menu-btn" @click="handleSave(true)" :disabled="!mapStore.hasMap" title="Save As (Ctrl+Shift+S)">Save As</button>
                </div>
            </div>
            <div class="menu-right">
            </div>
        </header>

        <!-- Toolbar -->
        <Toolbar />

        <!-- Message banner (load/save failures, fill limit) -->
        <div class="message-banner" v-if="mapStore.lastError">
            <span>{{ mapStore.lastError }}</span>
            <button class="message-close" @click="mapStore.clearError()" title="Dismiss">&times;</button>
        </div>

        <!-- Main content area -->
        <div class="content-area">
            <!-- Left sidebar -->
            <aside class="sidebar left-sidebar" v-if="editorStore.showTilePalette">
                <TilePalette />
            </aside>

            <!-- Map viewport (center) -->
            <main class="viewport-container">
                <MapViewport />
                <!-- Floating properties panel -->
                <PropertiesPanel v-if="editorStore.showProperties" class="floating-properties" />
                <!-- Floating minimap (when popped out) -->
                <Minimap v-if="editorStore.showMinimap && minimapFloating" class="floating-minimap" :floating="true" @toggle-float="minimapFloating = false" />
            </main>

            <!-- Right sidebar resize handle -->
            <div
                class="sidebar-resize-handle"
                :class="{ resizing: isResizingRightSidebar }"
                @mousedown="startRightSidebarResize"
            ></div>

            <!-- Right sidebar -->
            <aside
                class="sidebar right-sidebar"
                :class="{ resizing: isResizingRightSidebar }"
                :style="{ width: rightSidebarWidth + 'px' }"
            >
                <!-- Show SpritesheetViewer for tiles, ObjectSelector for objects -->
                <SpritesheetViewer v-if="editorStore.paletteTab === 'tiles'" />
                <ObjectSelector v-else />
                <!-- Docked minimap (when not floating) -->
                <Minimap v-if="editorStore.showMinimap && !minimapFloating" :floating="false" @toggle-float="minimapFloating = true" />
            </aside>
        </div>

        <!-- Status bar -->
        <StatusBar />

        <!-- Dialogs -->
        <NewMapDialog
            v-if="showNewMapDialog"
            @create="handleCreateMap"
            @cancel="showNewMapDialog = false"
        />
    </div>
</template>

<style scoped>
.editor-layout {
    display: flex;
    flex-direction: column;
    height: 100vh;
    width: 100vw;
    overflow: hidden;
    outline: none;
    position: relative;
}

.drop-zone-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(99, 102, 241, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    pointer-events: none;
}

.drop-zone-content {
    text-align: center;
    color: white;
}

.drop-icon {
    font-size: 64px;
    margin-bottom: 16px;
}

.drop-zone-content p {
    font-size: 24px;
    font-weight: 600;
}

.message-banner {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 6px 12px;
    background: rgba(239, 68, 68, 0.15);
    border-bottom: 1px solid var(--error-color);
    color: var(--text-primary);
    font-size: 12px;
}

.message-close {
    background: transparent;
    border: none;
    color: var(--text-secondary);
    font-size: 16px;
    line-height: 1;
    padding: 0 4px;
}

.message-close:hover {
    color: var(--text-primary);
}

.menu-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 36px;
    background: var(--bg-secondary);
    border-bottom: 1px solid var(--border-color);
    padding: 0 12px;
}

.menu-left {
    display: flex;
    align-items: center;
    gap: 16px;
}

.logo-link {
    display: flex;
    align-items: center;
    text-decoration: none;
}

.logo-img {
    width: 24px;
    height: 24px;
    image-rendering: pixelated;
}

.discord-link {
    font-size: 12px;
    color: #8b9eff;
    text-decoration: none;
    padding: 4px 8px;
    border-radius: 4px;
    transition: all 0.15s ease;
}

.discord-link:hover {
    background: rgba(139, 158, 255, 0.15);
    color: #b4c2ff;
}

.menu-items {
    display: flex;
    gap: 4px;
}

.menu-btn {
    background: transparent;
    border: none;
    color: var(--text-secondary);
    padding: 6px 12px;
    font-size: 13px;
    border-radius: 4px;
    transition: all 0.15s ease;
}

.menu-btn:hover:not(:disabled) {
    background: var(--bg-tertiary);
    color: var(--text-primary);
}

.menu-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.menu-right {
    display: flex;
    align-items: center;
}

.content-area {
    display: flex;
    flex: 1;
    overflow: hidden;
}

.sidebar {
    background: var(--bg-panel);
    border-color: var(--border-color);
    overflow-y: auto;
}

.left-sidebar {
    border-right: 1px solid var(--border-color);
    flex-shrink: 0;
}

.sidebar-resize-handle {
    width: 6px;
    background: var(--border-color);
    cursor: ew-resize;
    flex-shrink: 0;
    transition: background 0.15s ease;
}

.sidebar-resize-handle:hover,
.sidebar-resize-handle.resizing {
    background: var(--accent-color);
}

.right-sidebar {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.right-sidebar.resizing {
    user-select: none;
}

.viewport-container {
    flex: 1;
    overflow: hidden;
    position: relative;
}

.floating-properties {
    position: absolute;
    top: 12px;
    left: 12px;
    z-index: 100;
    background: rgba(30, 30, 46, 0.85);
    backdrop-filter: blur(8px);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    max-width: 220px;
    pointer-events: auto;
}

.floating-minimap {
    position: absolute;
    bottom: 12px;
    right: 12px;
    z-index: 100;
    background: rgba(30, 30, 46, 0.85);
    backdrop-filter: blur(8px);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    pointer-events: auto;
}
</style>
